import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLeague } from '@/contexts/LeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import {
  ArrowLeftRight,
  Check,
  X,
  Send,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  Inbox,
  ShieldAlert,
  ThumbsUp,
  ThumbsDown,
  Timer,
  CalendarX,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface TradePlayer {
  id: string;
  player_id: string;
  full_name: string;
  position_abbreviation: string;
  mlb_team_abbreviation: string | null;
  headshot_url: string | null;
  from_team_id: string;
  to_team_id: string;
  total_trash_points: number;
  games_played: number;
}

interface TradeVote {
  id: string;
  trade_id: string;
  team_id: string;
  vote: string;
}

interface Trade {
  id: string;
  league_id: string;
  proposer_team_id: string;
  proposer_team_name: string;
  receiver_team_id: string;
  receiver_team_name: string;
  status: string;
  message: string | null;
  created_at: string;
  responded_at: string | null;
  review_deadline: string | null;
  veto_by: string | null;
  votes_for: number;
  votes_against: number;
  players: TradePlayer[];
  votes?: TradeVote[];
}

interface RosterPlayer {
  assignment_id: string;
  player_id: string;
  full_name: string;
  position_abbreviation: string;
  mlb_team_abbreviation: string | null;
  headshot_url: string | null;
  slot: string;
}

const positionColors: Record<string, string> = {
  C: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  '1B': 'bg-green-500/20 text-green-400 border-green-500/30',
  '2B': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  '3B': 'bg-red-500/20 text-red-400 border-red-500/30',
  SS: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  LF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  CF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  RF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  OF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  DH: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  SP: 'bg-red-500/20 text-red-400 border-red-500/30',
  RP: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  P: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const MLBBetaTrades = () => {
  const { activeLeague, teams } = useLeague();
  const { user } = useAuth();
  const { toast } = useToast();

  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [tab, setTab] = useState('inbox');

  // Trade review settings
  const [tradeReviewHours, setTradeReviewHours] = useState(24);
  const [tradeReviewType, setTradeReviewType] = useState('commissioner_veto');
  const [tradeDeadline, setTradeDeadline] = useState<string | null>(null);

  const tradesPastDeadline = tradeDeadline ? new Date() > new Date(tradeDeadline) : false;

  // Propose trade state
  const [showPropose, setShowPropose] = useState(false);
  const [targetTeamId, setTargetTeamId] = useState('');
  const [myRoster, setMyRoster] = useState<RosterPlayer[]>([]);
  const [theirRoster, setTheirRoster] = useState<RosterPlayer[]>([]);
  const [myOffered, setMyOffered] = useState<Set<string>>(new Set());
  const [theirOffered, setTheirOffered] = useState<Set<string>>(new Set());
  const [tradeMessage, setTradeMessage] = useState('');
  const [rostersLoading, setRostersLoading] = useState(false);

  const myTeam = teams.find(t => t.user_id === user?.id);
  const otherTeams = teams.filter(t => t.user_id !== user?.id);
  const isCommissioner = activeLeague?.commissioner_id === user?.id;

  // Fetch league trade review settings
  useEffect(() => {
    if (!activeLeague) return;
    const fetchSettings = async () => {
      const { data } = await supabase
        .from('leagues')
        .select('trade_review_hours, trade_review_type, trade_deadline')
        .eq('id', activeLeague.id)
        .single();
      if (data) {
        setTradeReviewHours((data as any).trade_review_hours ?? 24);
        setTradeReviewType((data as any).trade_review_type ?? 'commissioner_veto');
        setTradeDeadline((data as any).trade_deadline || null);
      }
    };
    fetchSettings();
  }, [activeLeague]);

  // Fetch trades
  const fetchTrades = useCallback(async () => {
    if (!activeLeague) { setLoading(false); return; }

    const { data: tradesData, error } = await supabase
      .from('trades')
      .select('*')
      .eq('league_id', activeLeague.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to fetch trades:', error);
      setLoading(false);
      return;
    }

    // Fetch trade players for all trades
    const tradeIds = (tradesData || []).map(t => t.id);
    let tradePlayers: any[] = [];
    if (tradeIds.length > 0) {
      const { data: tp } = await supabase
        .from('trade_players')
        .select('*, players!inner(full_name, position_abbreviation, mlb_team_abbreviation, headshot_url)')
        .in('trade_id', tradeIds);
      tradePlayers = tp || [];
    }

    // Fetch season trash points for all players in trades
    const allPlayerIds = [...new Set(tradePlayers.map(tp => tp.player_id))];
    let playerStatsMap: Record<string, { total_trash_points: number; games_played: number }> = {};
    if (allPlayerIds.length > 0) {
      const { data: gameStats } = await supabase
        .from('player_game_stats')
        .select('player_id, trash_points')
        .in('player_id', allPlayerIds);

      for (const gs of (gameStats || [])) {
        if (!playerStatsMap[gs.player_id]) {
          playerStatsMap[gs.player_id] = { total_trash_points: 0, games_played: 0 };
        }
        playerStatsMap[gs.player_id].total_trash_points += Number(gs.trash_points || 0);
        playerStatsMap[gs.player_id].games_played += 1;
      }
    }

    const mapped: Trade[] = (tradesData || []).map(t => {
      const proposerTeam = teams.find(tm => tm.id === t.proposer_team_id);
      const receiverTeam = teams.find(tm => tm.id === t.receiver_team_id);
      const players = tradePlayers
        .filter(tp => tp.trade_id === t.id)
        .map((tp: any) => ({
          id: tp.id,
          player_id: tp.player_id,
          full_name: tp.players.full_name,
          position_abbreviation: tp.players.position_abbreviation,
          mlb_team_abbreviation: tp.players.mlb_team_abbreviation,
          headshot_url: tp.players.headshot_url,
          from_team_id: tp.from_team_id,
          to_team_id: tp.to_team_id,
          total_trash_points: playerStatsMap[tp.player_id]?.total_trash_points || 0,
          games_played: playerStatsMap[tp.player_id]?.games_played || 0,
        }));

      return {
        id: t.id,
        league_id: t.league_id,
        proposer_team_id: t.proposer_team_id,
        proposer_team_name: proposerTeam?.name || 'Unknown',
        receiver_team_id: t.receiver_team_id,
        receiver_team_name: receiverTeam?.name || 'Unknown',
        status: t.status,
        message: t.message,
        created_at: t.created_at,
        responded_at: t.responded_at,
        review_deadline: (t as any).review_deadline || null,
        veto_by: (t as any).veto_by || null,
        votes_for: (t as any).votes_for || 0,
        votes_against: (t as any).votes_against || 0,
        players,
      };
    });

    setTrades(mapped);
    setLoading(false);
  }, [activeLeague, teams]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  // Realtime subscription
  useEffect(() => {
    if (!activeLeague) return;
    const channel = supabase
      .channel(`trades-realtime-${activeLeague.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'trades', filter: `league_id=eq.${activeLeague.id}` }, () => {
        fetchTrades();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeLeague?.id]);

  // Fetch rosters when target team changes
  useEffect(() => {
    const fetchRosters = async () => {
      if (!myTeam || !targetTeamId) return;
      setRostersLoading(true);

      const [{ data: mine }, { data: theirs }] = await Promise.all([
        supabase
          .from('roster_assignments')
          .select('id, player_id, slot, players!inner(full_name, position_abbreviation, mlb_team_abbreviation, headshot_url)')
          .eq('team_id', myTeam.id),
        supabase
          .from('roster_assignments')
          .select('id, player_id, slot, players!inner(full_name, position_abbreviation, mlb_team_abbreviation, headshot_url)')
          .eq('team_id', targetTeamId),
      ]);

      const mapRoster = (data: any[]): RosterPlayer[] =>
        (data || []).map((r: any) => ({
          assignment_id: r.id,
          player_id: r.player_id,
          full_name: r.players.full_name,
          position_abbreviation: r.players.position_abbreviation,
          mlb_team_abbreviation: r.players.mlb_team_abbreviation,
          headshot_url: r.players.headshot_url,
          slot: r.slot,
        }));

      setMyRoster(mapRoster(mine || []));
      setTheirRoster(mapRoster(theirs || []));
      setMyOffered(new Set());
      setTheirOffered(new Set());
      setRostersLoading(false);
    };
    fetchRosters();
  }, [myTeam, targetTeamId]);

  const toggleMyPlayer = (playerId: string) => {
    setMyOffered(prev => {
      const next = new Set(prev);
      next.has(playerId) ? next.delete(playerId) : next.add(playerId);
      return next;
    });
  };

  const toggleTheirPlayer = (playerId: string) => {
    setTheirOffered(prev => {
      const next = new Set(prev);
      next.has(playerId) ? next.delete(playerId) : next.add(playerId);
      return next;
    });
  };

  // Propose trade
  const handlePropose = async () => {
    if (!myTeam || !activeLeague || !targetTeamId || myOffered.size === 0 || theirOffered.size === 0 || tradesPastDeadline) return;
    setProcessing(true);

    // Create trade
    const { data: trade, error: tradeErr } = await supabase
      .from('trades')
      .insert({
        league_id: activeLeague.id,
        proposer_team_id: myTeam.id,
        receiver_team_id: targetTeamId,
        message: tradeMessage || null,
      })
      .select('id')
      .single();

    if (tradeErr || !trade) {
      toast({ title: 'Trade failed', description: tradeErr?.message || 'Could not create trade', variant: 'destructive' });
      setProcessing(false);
      return;
    }

    // Insert trade players
    const playerRows = [
      ...Array.from(myOffered).map(pid => ({
        trade_id: trade.id,
        player_id: pid,
        from_team_id: myTeam.id,
        to_team_id: targetTeamId,
      })),
      ...Array.from(theirOffered).map(pid => ({
        trade_id: trade.id,
        player_id: pid,
        from_team_id: targetTeamId,
        to_team_id: myTeam.id,
      })),
    ];

    const { error: playersErr } = await supabase
      .from('trade_players')
      .insert(playerRows);

    if (playersErr) {
      toast({ title: 'Trade failed', description: playersErr.message, variant: 'destructive' });
      setProcessing(false);
      return;
    }

    // Log activity
    const myPlayerNames = myRoster.filter(p => myOffered.has(p.player_id)).map(p => p.full_name);
    const theirPlayerNames = theirRoster.filter(p => theirOffered.has(p.player_id)).map(p => p.full_name);
    const receiverTeam = teams.find(t => t.id === targetTeamId);

    await supabase.from('league_activity').insert({
      league_id: activeLeague.id,
      team_id: myTeam.id,
      activity_type: 'trade_proposed',
      details: {
        proposer_team: myTeam.name,
        receiver_team: receiverTeam?.name,
        proposer_team_id: myTeam.id,
        receiver_team_id: targetTeamId,
        offered: myPlayerNames,
        requested: theirPlayerNames,
      },
    } as any);

    // Notify the receiving team about the trade proposal
    if (receiverTeam) {
      await supabase.from('notifications').insert({
        user_id: receiverTeam.user_id,
        league_id: activeLeague.id,
        type: 'trade_received',
        title: 'New Trade Offer',
        message: `${myTeam.name} wants to trade ${myPlayerNames.join(', ')} for ${theirPlayerNames.join(', ')}`,
        metadata: { trade_id: trade.id },
      } as any);
    }

    trackEvent('trade_proposed', { trade_id: trade.id, players_offered: myOffered.size, players_requested: theirOffered.size });
    toast({ title: 'Trade proposed!', description: `Sent to ${receiverTeam?.name}` });
    setShowPropose(false);
    setTargetTeamId('');
    setMyOffered(new Set());
    setTheirOffered(new Set());
    setTradeMessage('');
    setProcessing(false);
    await fetchTrades();
  };

  // Accept trade — move to review period instead of immediate execution
  const handleAccept = async (trade: Trade) => {
    if (!myTeam || !activeLeague) return;
    setProcessing(true);

    // Calculate review deadline
    const deadline = new Date();
    deadline.setHours(deadline.getHours() + tradeReviewHours);

    // Set trade to under_review with a deadline
    const { error: updateErr } = await supabase
      .from('trades')
      .update({
        status: 'under_review',
        responded_at: new Date().toISOString(),
        review_deadline: deadline.toISOString(),
      } as any)
      .eq('id', trade.id);

    if (updateErr) {
      toast({ title: 'Failed', description: updateErr.message, variant: 'destructive' });
      setProcessing(false);
      return;
    }

    // Log activity
    await supabase.from('league_activity').insert({
      league_id: activeLeague.id,
      team_id: myTeam.id,
      activity_type: 'trade_accepted_review',
      details: {
        proposer_team: trade.proposer_team_name,
        receiver_team: trade.receiver_team_name,
        proposer_team_id: trade.proposer_team_id,
        receiver_team_id: trade.receiver_team_id,
        review_hours: tradeReviewHours,
        review_type: tradeReviewType,
      },
    } as any);

    // Notify the proposer that the trade was accepted
    const proposerTeam = teams.find(t => t.id === trade.proposer_team_id);
    if (proposerTeam) {
      await supabase.from('notifications').insert({
        user_id: proposerTeam.user_id,
        league_id: activeLeague.id,
        type: 'trade_accepted',
        title: 'Trade Accepted!',
        message: `${trade.receiver_team_name} accepted your trade. Under review for ${tradeReviewHours}h.`,
        metadata: { trade_id: trade.id },
      } as any);
    }

    trackEvent('trade_accepted', { trade_id: trade.id });
    toast({
      title: 'Trade accepted, under review',
      description: `Trade will be finalized in ${tradeReviewHours}h unless vetoed.`,
    });
    setProcessing(false);
    await fetchTrades();
  };

  // Commissioner veto
  const handleVeto = async (trade: Trade) => {
    if (!myTeam || !activeLeague || !isCommissioner) return;
    setProcessing(true);

    const { error } = await supabase
      .from('trades')
      .update({
        status: 'vetoed',
        veto_by: user?.id,
        responded_at: new Date().toISOString(),
      } as any)
      .eq('id', trade.id);

    if (error) {
      toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    } else {
      await supabase.from('league_activity').insert({
        league_id: activeLeague.id,
        team_id: myTeam.id,
        activity_type: 'trade_vetoed',
        details: {
          proposer_team: trade.proposer_team_name,
          receiver_team: trade.receiver_team_name,
          proposer_team_id: trade.proposer_team_id,
          receiver_team_id: trade.receiver_team_id,
          vetoed_by: 'Commissioner',
        },
      } as any);
      toast({ title: 'Trade vetoed', description: 'The trade has been blocked.' });
    }
    setProcessing(false);
    await fetchTrades();
  };

  // League member vote on trade
  const handleVote = async (trade: Trade, vote: 'approve' | 'veto') => {
    if (!myTeam || !activeLeague) return;
    setProcessing(true);

    // Upsert vote
    const { error } = await supabase
      .from('trade_votes')
      .upsert({
        trade_id: trade.id,
        team_id: myTeam.id,
        vote,
      } as any, { onConflict: 'trade_id,team_id' });

    if (error) {
      toast({ title: 'Vote failed', description: error.message, variant: 'destructive' });
    } else {
      // Update vote counts on trade
      const { data: votes } = await supabase
        .from('trade_votes')
        .select('vote')
        .eq('trade_id', trade.id);

      const votesFor = (votes || []).filter(v => v.vote === 'approve').length;
      const votesAgainst = (votes || []).filter(v => v.vote === 'veto').length;

      await supabase
        .from('trades')
        .update({ votes_for: votesFor, votes_against: votesAgainst } as any)
        .eq('id', trade.id);

      toast({ title: vote === 'approve' ? 'Voted to approve' : 'Voted to veto' });
    }
    setProcessing(false);
    await fetchTrades();
  };

  // Counter trade state
  const [showCounter, setShowCounter] = useState(false);
  const [counterTrade, setCounterTrade] = useState<Trade | null>(null);
  const [counterMyRoster, setCounterMyRoster] = useState<RosterPlayer[]>([]);
  const [counterTheirRoster, setCounterTheirRoster] = useState<RosterPlayer[]>([]);
  const [counterMyOffered, setCounterMyOffered] = useState<Set<string>>(new Set());
  const [counterTheirOffered, setCounterTheirOffered] = useState<Set<string>>(new Set());
  const [counterMessage, setCounterMessage] = useState('');
  const [counterRostersLoading, setCounterRostersLoading] = useState(false);

  // Open counter trade dialog
  const handleOpenCounter = async (trade: Trade) => {
    if (!myTeam) return;
    setCounterTrade(trade);
    setCounterMessage('');
    setCounterRostersLoading(true);
    setShowCounter(true);

    const [{ data: mine }, { data: theirs }] = await Promise.all([
      supabase
        .from('roster_assignments')
        .select('id, player_id, slot, players!inner(full_name, position_abbreviation, mlb_team_abbreviation, headshot_url)')
        .eq('team_id', myTeam.id),
      supabase
        .from('roster_assignments')
        .select('id, player_id, slot, players!inner(full_name, position_abbreviation, mlb_team_abbreviation, headshot_url)')
        .eq('team_id', trade.proposer_team_id),
    ]);

    const mapRoster = (data: any[]): RosterPlayer[] =>
      (data || []).map((r: any) => ({
        assignment_id: r.id,
        player_id: r.player_id,
        full_name: r.players.full_name,
        position_abbreviation: r.players.position_abbreviation,
        mlb_team_abbreviation: r.players.mlb_team_abbreviation,
        headshot_url: r.players.headshot_url,
        slot: r.slot,
      }));

    setCounterMyRoster(mapRoster(mine || []));
    setCounterTheirRoster(mapRoster(theirs || []));

    // Pre-select players from the original trade
    const myPlayerIds = trade.players.filter(p => p.from_team_id === myTeam.id).map(p => p.player_id);
    const theirPlayerIds = trade.players.filter(p => p.from_team_id === trade.proposer_team_id).map(p => p.player_id);
    setCounterMyOffered(new Set(myPlayerIds));
    setCounterTheirOffered(new Set(theirPlayerIds));
    setCounterRostersLoading(false);
  };

  // Submit counter trade
  const handleSubmitCounter = async () => {
    if (!myTeam || !activeLeague || !counterTrade || counterMyOffered.size === 0 || counterTheirOffered.size === 0) return;
    setProcessing(true);

    // Reject the original trade
    await supabase
      .from('trades')
      .update({ status: 'countered', responded_at: new Date().toISOString() })
      .eq('id', counterTrade.id);

    // Create new counter trade (receiver becomes proposer)
    const { data: newTrade, error: tradeErr } = await supabase
      .from('trades')
      .insert({
        league_id: activeLeague.id,
        proposer_team_id: myTeam.id,
        receiver_team_id: counterTrade.proposer_team_id,
        message: counterMessage || `Counter to previous offer`,
      })
      .select('id')
      .single();

    if (tradeErr || !newTrade) {
      toast({ title: 'Counter failed', description: tradeErr?.message || 'Could not create counter', variant: 'destructive' });
      setProcessing(false);
      return;
    }

    const playerRows = [
      ...Array.from(counterMyOffered).map(pid => ({
        trade_id: newTrade.id,
        player_id: pid,
        from_team_id: myTeam.id,
        to_team_id: counterTrade.proposer_team_id,
      })),
      ...Array.from(counterTheirOffered).map(pid => ({
        trade_id: newTrade.id,
        player_id: pid,
        from_team_id: counterTrade.proposer_team_id,
        to_team_id: myTeam.id,
      })),
    ];

    await supabase.from('trade_players').insert(playerRows);

    // Notify original proposer
    const proposerTeam = teams.find(t => t.id === counterTrade.proposer_team_id);
    if (proposerTeam) {
      const myPlayerNames = counterMyRoster.filter(p => counterMyOffered.has(p.player_id)).map(p => p.full_name);
      const theirPlayerNames = counterTheirRoster.filter(p => counterTheirOffered.has(p.player_id)).map(p => p.full_name);

      await supabase.from('notifications').insert({
        user_id: proposerTeam.user_id,
        league_id: activeLeague.id,
        type: 'trade_received',
        title: 'Counter Offer Received',
        message: `${myTeam.name} countered: ${myPlayerNames.join(', ')} for ${theirPlayerNames.join(', ')}`,
        metadata: { trade_id: newTrade.id },
      } as any);

      await supabase.from('league_activity').insert({
        league_id: activeLeague.id,
        team_id: myTeam.id,
        activity_type: 'trade_countered',
        details: {
          original_trade_id: counterTrade.id,
          counter_trade_id: newTrade.id,
          proposer_team: myTeam.name,
          receiver_team: proposerTeam.name,
          proposer_team_id: myTeam.id,
          receiver_team_id: proposerTeam.id,
        },
      } as any);
    }

    toast({ title: 'Counter offer sent!', description: `Sent to ${proposerTeam?.name}` });
    setShowCounter(false);
    setCounterTrade(null);
    setProcessing(false);
    await fetchTrades();
  };

  // Reject trade
  const handleReject = async (trade: Trade) => {
    setProcessing(true);
    const { error } = await supabase
      .from('trades')
      .update({ status: 'rejected', responded_at: new Date().toISOString() })
      .eq('id', trade.id);

    if (error) {
      toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    } else {
      // Notify the proposer
      const proposerTeam = teams.find(t => t.id === trade.proposer_team_id);
      if (proposerTeam) {
        await supabase.from('notifications').insert({
          user_id: proposerTeam.user_id,
          league_id: activeLeague?.id,
          type: 'trade_rejected',
          title: 'Trade Rejected',
          message: `${trade.receiver_team_name} rejected your trade offer.`,
          metadata: { trade_id: trade.id },
        } as any);
      }
      trackEvent('trade_rejected', { trade_id: trade.id });
      toast({ title: 'Trade rejected' });
    }
    setProcessing(false);
    await fetchTrades();
  };

  // Cancel trade (proposer)
  const handleCancel = async (trade: Trade) => {
    setProcessing(true);
    const { error } = await supabase
      .from('trades')
      .update({ status: 'cancelled', responded_at: new Date().toISOString() })
      .eq('id', trade.id);

    if (error) {
      toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Trade cancelled' });
    }
    setProcessing(false);
    await fetchTrades();
  };

  // Filter trades by tab
  const activeTrades = trades.filter(t =>
    (t.status === 'pending' || t.status === 'under_review') && myTeam &&
    (t.proposer_team_id === myTeam.id || t.receiver_team_id === myTeam.id ||
      // All league members see under_review trades for voting
      t.status === 'under_review')
  );
  const completedTrades = trades.filter(t => !['pending', 'under_review'].includes(t.status));

  if (loading) {
    return (
      <div className="space-y-3 pb-20 md:pb-0">
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold font-heading text-foreground">Trades</h1>
        {myTeam && !tradesPastDeadline && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setShowPropose(true)}
          >
            <ArrowLeftRight className="w-4 h-4" />
            Propose Trade
          </Button>
        )}
      </div>

      {tradesPastDeadline && tradeDeadline && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="p-4 flex items-center gap-3">
            <CalendarX className="w-5 h-5 text-destructive shrink-0" />
            <div>
              <p className="text-sm font-medium text-destructive">Trade Deadline Passed</p>
              <p className="text-xs text-muted-foreground">
                The trade deadline was {format(new Date(tradeDeadline), 'MMMM d, yyyy')}. No new trades can be proposed.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!tradesPastDeadline && tradeDeadline && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary shrink-0" />
            <p className="text-xs text-muted-foreground">
              Trade deadline: <span className="text-foreground font-medium">{format(new Date(tradeDeadline), 'MMMM d, yyyy')}</span>
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full">
          <TabsTrigger value="inbox" className="flex-1 gap-1.5">
            <Inbox className="w-3.5 h-3.5" />
            Inbox
            {activeTrades.length > 0 && (
              <Badge variant="destructive" className="text-[10px] px-1.5 h-4 ml-1">
                {activeTrades.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex-1 gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="space-y-3">
          {activeTrades.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center text-muted-foreground">
                <Inbox className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="font-medium text-foreground">No pending trades</p>
                <p className="text-sm mt-1">Propose a trade to get started.</p>
              </CardContent>
            </Card>
          ) : (
            activeTrades.map(trade => (
              <TradeCard
                key={trade.id}
                trade={trade}
                myTeamId={myTeam?.id || ''}
                isCommissioner={isCommissioner}
                tradeReviewType={tradeReviewType}
                onAccept={() => handleAccept(trade)}
                onReject={() => handleReject(trade)}
                onCancel={() => handleCancel(trade)}
                onVeto={() => handleVeto(trade)}
                onVote={(vote) => handleVote(trade, vote)}
                onCounter={() => handleOpenCounter(trade)}
                processing={processing}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-3">
          {completedTrades.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center text-muted-foreground">
                <p className="font-medium text-foreground">No trade history</p>
              </CardContent>
            </Card>
          ) : (
            completedTrades.map(trade => (
              <TradeCard
                key={trade.id}
                trade={trade}
                myTeamId={myTeam?.id || ''}
                isCommissioner={isCommissioner}
                onReverse={trade.status === 'completed' && isCommissioner ? async () => {
                  setProcessing(true);
                  const resp = await supabase.functions.invoke('commissioner-override', {
                    body: {
                      action: 'reverse_trade',
                      league_id: activeLeague?.id,
                      trade_id: trade.id,
                    },
                  });
                  if (resp.error || resp.data?.error) {
                    toast({ title: 'Reverse failed', description: resp.error?.message || resp.data?.error, variant: 'destructive' });
                  } else {
                    toast({ title: 'Trade reversed', description: 'Players returned to original teams.' });
                  }
                  setProcessing(false);
                  await fetchTrades();
                } : undefined}
                processing={processing}
              />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Propose Trade Dialog */}
      <Dialog open={showPropose} onOpenChange={setShowPropose}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Propose a Trade</DialogTitle>
            <DialogDescription>Select a team and pick players to trade.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Select value={targetTeamId} onValueChange={setTargetTeamId}>
              <SelectTrigger>
                <SelectValue placeholder="Select team to trade with..." />
              </SelectTrigger>
              <SelectContent>
                {otherTeams.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {targetTeamId && (
              <>
                {rostersLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* My Players */}
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-2">
                        You send ({myOffered.size})
                      </h3>
                      <div className="space-y-1 max-h-[35vh] overflow-y-auto">
                        {myRoster.map(p => (
                          <button
                            key={p.player_id}
                            onClick={() => toggleMyPlayer(p.player_id)}
                            className={cn(
                              "w-full flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-left text-sm",
                              myOffered.has(p.player_id)
                                ? "border-primary bg-primary/10"
                                : "border-border hover:border-primary/30"
                            )}
                          >
                            <PlayerAvatar player={p} />
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-foreground truncate block">{p.full_name}</span>
                              <Badge variant="outline" className={cn("text-[9px] px-1 h-4", positionColors[p.position_abbreviation])}>
                                {p.position_abbreviation}
                              </Badge>
                            </div>
                            {myOffered.has(p.player_id) && <Check className="w-4 h-4 text-primary shrink-0" />}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Their Players */}
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-2">
                        You receive ({theirOffered.size})
                      </h3>
                      <div className="space-y-1 max-h-[35vh] overflow-y-auto">
                        {theirRoster.map(p => (
                          <button
                            key={p.player_id}
                            onClick={() => toggleTheirPlayer(p.player_id)}
                            className={cn(
                              "w-full flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-left text-sm",
                              theirOffered.has(p.player_id)
                                ? "border-primary bg-primary/10"
                                : "border-border hover:border-primary/30"
                            )}
                          >
                            <PlayerAvatar player={p} />
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-foreground truncate block">{p.full_name}</span>
                              <Badge variant="outline" className={cn("text-[9px] px-1 h-4", positionColors[p.position_abbreviation])}>
                                {p.position_abbreviation}
                              </Badge>
                            </div>
                            {theirOffered.has(p.player_id) && <Check className="w-4 h-4 text-primary shrink-0" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <Textarea
                  placeholder="Add a message (optional)..."
                  value={tradeMessage}
                  onChange={(e) => setTradeMessage(e.target.value)}
                  className="resize-none"
                  rows={2}
                />

                <Button
                  className="w-full gap-2"
                  disabled={myOffered.size === 0 || theirOffered.size === 0 || processing}
                  onClick={handlePropose}
                >
                  <Send className="w-4 h-4" />
                  {processing ? 'Sending...' : `Send Trade (${myOffered.size} for ${theirOffered.size})`}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Counter Trade Dialog */}
      <Dialog open={showCounter} onOpenChange={(open) => { setShowCounter(open); if (!open) setCounterTrade(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Counter Trade Offer</DialogTitle>
            <DialogDescription>
              Modify the terms and send a counter-offer to {counterTrade?.proposer_team_name}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {counterRostersLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* My Players (receiver) */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">
                    You send ({counterMyOffered.size})
                  </h3>
                  <div className="space-y-1 max-h-[35vh] overflow-y-auto">
                    {counterMyRoster.map(p => (
                      <button
                        key={p.player_id}
                        onClick={() => {
                          setCounterMyOffered(prev => {
                            const next = new Set(prev);
                            next.has(p.player_id) ? next.delete(p.player_id) : next.add(p.player_id);
                            return next;
                          });
                        }}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-left text-sm",
                          counterMyOffered.has(p.player_id)
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-primary/30"
                        )}
                      >
                        <PlayerAvatar player={p} />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-foreground truncate block">{p.full_name}</span>
                          <Badge variant="outline" className={cn("text-[9px] px-1 h-4", positionColors[p.position_abbreviation])}>
                            {p.position_abbreviation}
                          </Badge>
                        </div>
                        {counterMyOffered.has(p.player_id) && <Check className="w-4 h-4 text-primary shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Their Players (original proposer) */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">
                    You receive ({counterTheirOffered.size})
                  </h3>
                  <div className="space-y-1 max-h-[35vh] overflow-y-auto">
                    {counterTheirRoster.map(p => (
                      <button
                        key={p.player_id}
                        onClick={() => {
                          setCounterTheirOffered(prev => {
                            const next = new Set(prev);
                            next.has(p.player_id) ? next.delete(p.player_id) : next.add(p.player_id);
                            return next;
                          });
                        }}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-left text-sm",
                          counterTheirOffered.has(p.player_id)
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-primary/30"
                        )}
                      >
                        <PlayerAvatar player={p} />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-foreground truncate block">{p.full_name}</span>
                          <Badge variant="outline" className={cn("text-[9px] px-1 h-4", positionColors[p.position_abbreviation])}>
                            {p.position_abbreviation}
                          </Badge>
                        </div>
                        {counterTheirOffered.has(p.player_id) && <Check className="w-4 h-4 text-primary shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <Textarea
              placeholder="Add a message with your counter (optional)..."
              value={counterMessage}
              onChange={(e) => setCounterMessage(e.target.value)}
              className="resize-none"
              rows={2}
            />

            <Button
              className="w-full gap-2"
              disabled={counterMyOffered.size === 0 || counterTheirOffered.size === 0 || processing}
              onClick={handleSubmitCounter}
            >
              <ArrowLeftRight className="w-4 h-4" />
              {processing ? 'Sending...' : `Send Counter (${counterMyOffered.size} for ${counterTheirOffered.size})`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Trade card component
const TradeCard = ({
  trade,
  myTeamId,
  isCommissioner,
  tradeReviewType,
  onAccept,
  onReject,
  onCancel,
  onVeto,
  onVote,
  onReverse,
  onCounter,
  processing,
}: {
  trade: Trade;
  myTeamId: string;
  isCommissioner?: boolean;
  tradeReviewType?: string;
  onAccept?: () => void;
  onReject?: () => void;
  onCancel?: () => void;
  onVeto?: () => void;
  onVote?: (vote: 'approve' | 'veto') => void;
  onReverse?: () => void;
  onCounter?: () => void;
  processing?: boolean;
}) => {
  const isReceiver = trade.receiver_team_id === myTeamId;
  const isProposer = trade.proposer_team_id === myTeamId;
  const isPending = trade.status === 'pending';
  const isUnderReview = trade.status === 'under_review';
  const isParticipant = isReceiver || isProposer;

  const proposerPlayers = trade.players.filter(p => p.from_team_id === trade.proposer_team_id);
  const receiverPlayers = trade.players.filter(p => p.from_team_id === trade.receiver_team_id);

  // Point totals for trade analysis
  const proposerTotal = proposerPlayers.reduce((sum, p) => sum + p.total_trash_points, 0);
  const receiverTotal = receiverPlayers.reduce((sum, p) => sum + p.total_trash_points, 0);
  const pointDiff = proposerTotal - receiverTotal;

  // Calculate time remaining for review
  const getTimeRemaining = () => {
    if (!trade.review_deadline) return null;
    const deadline = new Date(trade.review_deadline);
    const now = new Date();
    const diff = deadline.getTime() - now.getTime();
    if (diff <= 0) return 'Processing...';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m remaining`;
  };

  const statusConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
    pending: { icon: <Clock className="w-3.5 h-3.5" />, label: 'Pending', color: 'text-yellow-400' },
    under_review: { icon: <Timer className="w-3.5 h-3.5" />, label: 'Under Review', color: 'text-blue-400' },
    completed: { icon: <CheckCircle2 className="w-3.5 h-3.5" />, label: 'Completed', color: 'text-success' },
    accepted: { icon: <CheckCircle2 className="w-3.5 h-3.5" />, label: 'Accepted', color: 'text-success' },
    rejected: { icon: <XCircle className="w-3.5 h-3.5" />, label: 'Rejected', color: 'text-destructive' },
    vetoed: { icon: <ShieldAlert className="w-3.5 h-3.5" />, label: 'Vetoed', color: 'text-destructive' },
    cancelled: { icon: <XCircle className="w-3.5 h-3.5" />, label: 'Cancelled', color: 'text-muted-foreground' },
    countered: { icon: <ArrowLeftRight className="w-3.5 h-3.5" />, label: 'Countered', color: 'text-primary' },
  };

  const status = statusConfig[trade.status] || statusConfig.pending;

  return (
    <Card className="bg-card border-border overflow-hidden">
      <div className="px-4 py-2.5 bg-muted/30 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ArrowLeftRight className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">
            {trade.proposer_team_name}
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">
            {trade.receiver_team_name}
          </span>
        </div>
        <div className={cn("flex items-center gap-1 text-xs font-medium", status.color)}>
          {status.icon}
          {status.label}
        </div>
      </div>

      <CardContent className="p-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Proposer sends */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
              {trade.proposer_team_name} sends
            </p>
            <div className="space-y-1">
              {proposerPlayers.map(p => (
                <div key={p.id} className="flex items-center gap-2">
                  <PlayerAvatar player={p} size="sm" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-foreground truncate block">{p.full_name}</span>
                    <Badge variant="outline" className={cn("text-[8px] px-1 h-3.5", positionColors[p.position_abbreviation])}>
                      {p.position_abbreviation}
                    </Badge>
                  </div>
                  <span className="text-xs font-semibold text-foreground shrink-0">
                    {p.total_trash_points.toFixed(1)}
                    <span className="text-[9px] text-muted-foreground ml-0.5">TP</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-1.5 border-t border-border/30 flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground font-medium">Total</span>
              <span className="text-xs font-bold text-foreground">
                {proposerTotal.toFixed(1)} TP
              </span>
            </div>
          </div>

          {/* Receiver sends */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
              {trade.receiver_team_name} sends
            </p>
            <div className="space-y-1">
              {receiverPlayers.map(p => (
                <div key={p.id} className="flex items-center gap-2">
                  <PlayerAvatar player={p} size="sm" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-foreground truncate block">{p.full_name}</span>
                    <Badge variant="outline" className={cn("text-[8px] px-1 h-3.5", positionColors[p.position_abbreviation])}>
                      {p.position_abbreviation}
                    </Badge>
                  </div>
                  <span className="text-xs font-semibold text-foreground shrink-0">
                    {p.total_trash_points.toFixed(1)}
                    <span className="text-[9px] text-muted-foreground ml-0.5">TP</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-1.5 border-t border-border/30 flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground font-medium">Total</span>
              <span className="text-xs font-bold text-foreground">
                {receiverTotal.toFixed(1)} TP
              </span>
            </div>
          </div>
        </div>

        {/* Trade Analysis Summary */}
        <div className="mt-3 p-2.5 rounded-lg bg-muted/40 border border-border/50">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Trade Analysis</span>
            <Badge
              variant="outline"
              className={cn("text-[10px] px-1.5 h-4", 
                Math.abs(pointDiff) < 5 ? 'text-success border-success/30' :
                Math.abs(pointDiff) < 15 ? 'text-yellow-400 border-yellow-500/30' :
                'text-destructive border-destructive/30'
              )}
            >
              {Math.abs(pointDiff) < 5 ? 'Fair' : Math.abs(pointDiff) < 15 ? 'Slight Edge' : 'Lopsided'}
            </Badge>
          </div>
          <div className="flex items-center justify-between mt-1.5 text-xs">
            <span className="text-muted-foreground">Point differential</span>
            <span className={cn("font-semibold",
              Math.abs(pointDiff) < 5 ? 'text-success' : 'text-foreground'
            )}>
              {pointDiff > 0 ? '+' : ''}{pointDiff.toFixed(1)} TP
            </span>
          </div>
        </div>

        {trade.message && (
          <p className="text-xs text-muted-foreground mt-3 italic border-t border-border/30 pt-2">
            "{trade.message}"
          </p>
        )}

        {/* Review period info */}
        {isUnderReview && (
          <div className="mt-3 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs text-blue-400 font-medium">
                <Timer className="w-3.5 h-3.5" />
                {getTimeRemaining()}
              </div>
              {(trade.votes_for > 0 || trade.votes_against > 0) && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="flex items-center gap-0.5 text-success">
                    <ThumbsUp className="w-3 h-3" /> {trade.votes_for}
                  </span>
                  <span className="flex items-center gap-0.5 text-destructive">
                    <ThumbsDown className="w-3 h-3" /> {trade.votes_against}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/30">
          <span className="text-[10px] text-muted-foreground">
            {new Date(trade.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </span>

          <div className="flex gap-2">
            {/* Pending: receiver can accept/reject, proposer can cancel */}
            {isPending && isReceiver && onAccept && onReject && (
              <>
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-destructive" onClick={onReject} disabled={processing}>
                  <X className="w-3 h-3" /> Reject
                </Button>
                {onCounter && (
                  <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={onCounter} disabled={processing}>
                    <ArrowLeftRight className="w-3 h-3" /> Counter
                  </Button>
                )}
                <Button size="sm" className="h-7 gap-1 text-xs" onClick={onAccept} disabled={processing}>
                  <Check className="w-3 h-3" /> Accept
                </Button>
              </>
            )}
            {isPending && isProposer && onCancel && (
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={onCancel} disabled={processing}>
                <X className="w-3 h-3" /> Cancel
              </Button>
            )}

            {/* Under Review: commissioner veto or league vote */}
            {isUnderReview && isCommissioner && tradeReviewType === 'commissioner_veto' && onVeto && (
              <Button size="sm" variant="destructive" className="h-7 gap-1 text-xs" onClick={onVeto} disabled={processing}>
                <ShieldAlert className="w-3 h-3" /> Veto
              </Button>
            )}
            {isUnderReview && tradeReviewType === 'league_vote' && !isParticipant && onVote && (
              <>
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-success hover:text-success" onClick={() => onVote('approve')} disabled={processing}>
                  <ThumbsUp className="w-3 h-3" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-destructive hover:text-destructive" onClick={() => onVote('veto')} disabled={processing}>
                  <ThumbsDown className="w-3 h-3" /> Veto
                </Button>
              </>
            )}

            {/* Commissioner: reverse completed trades */}
            {trade.status === 'completed' && isCommissioner && onReverse && (
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={onReverse} disabled={processing}>
                <ShieldAlert className="w-3 h-3" /> Reverse
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Player avatar helper
const PlayerAvatar = ({ player, size = 'md' }: { player: { headshot_url: string | null; full_name: string }; size?: 'sm' | 'md' }) => {
  const dim = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';
  const textSize = size === 'sm' ? 'text-[8px]' : 'text-[10px]';

  if (player.headshot_url) {
    return (
      <img
        src={player.headshot_url}
        alt={player.full_name}
        className={cn(dim, "rounded-full bg-muted object-cover shrink-0")}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <div className={cn(dim, "rounded-full bg-muted flex items-center justify-center shrink-0")}>
      <span className={cn(textSize, "font-bold text-muted-foreground")}>
        {player.full_name.split(' ').map(n => n[0]).join('')}
      </span>
    </div>
  );
};

export default MLBBetaTrades;
